// Fixed ResetPassword.js with consistent field styling

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './ResetPassword.module.css';
import routes from 'routes';
import { resetPassword, getMemberInfoForSetPassword } from 'api/authApi';
import MetaTags from 'components/common/MetaTags';
import CustomInput from 'components/common/CustomInput/CustomInput';
import CustomButton, {
  defaultButtonStyle,
} from 'components/common/CustomButton';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const email = searchParams.get('email');

  const [loading, setLoading] = useState(false);
  const [memberInfo, setMemberInfo] = useState(null);
  const [needsNameCollection, setNeedsNameCollection] = useState(false);
  const [loadingMemberInfo, setLoadingMemberInfo] = useState(true);

  const [formValues, setFormValues] = useState({
    email: email || '',
    code: '',
    firstName: '',
    lastName: '',
    password: '',
    repeat_password: '',
  });

  // Clear autofilled values on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setFormValues((prev) => ({
        email: email || '',
        code: '',
        firstName: '',
        lastName: '',
        password: '',
        repeat_password: '',
      }));
    }, 100);

    return () => clearTimeout(timer);
  }, [email]);

  const [errors, setErrors] = useState({});

  // Check member info and determine what fields to show
  useEffect(() => {
    const checkSetupStatus = async () => {
      if (!email) {
        setLoadingMemberInfo(false);
        return;
      }

      try {
        setLoadingMemberInfo(true);

        // Try to decode email if it's base64 encoded, otherwise use as-is
        let decodedEmail = email;
        try {
          // Check if email looks like base64 (no @ symbol) and try to decode
          if (!email.includes('@')) {
            decodedEmail = atob(email);
          }
        } catch (decodeError) {
          // If decode fails, use email as-is
          console.log('Email not base64 encoded, using as plain text:', email);
          decodedEmail = email;
        }

        // API now works for both password reset and initial setup
        const response = await getMemberInfoForSetPassword(decodedEmail);
        const memberData = response.data;

        setMemberInfo(memberData);

        // Check if user needs to provide their name (always required if missing)
        const needsName = !memberData.firstName || !memberData.lastName;
        setNeedsNameCollection(needsName);

        // Pre-populate form if name exists
        if (!needsName) {
          setFormValues((prev) => ({
            ...prev,
            firstName: memberData.firstName || '',
            lastName: memberData.lastName || '',
          }));
        }

        console.log('Setup status determined:', {
          hasPassword: memberData.hasSetPassword,
          needsName: needsName,
          email: decodedEmail,
        });
      } catch (error) {
        console.error('Failed to fetch member info:', error);
        toast.error('Failed to load member information');

        // Fallback to basic reset flow
        setMemberInfo(null);
        setNeedsNameCollection(false);
      } finally {
        setLoadingMemberInfo(false);
      }
    };

    checkSetupStatus();
  }, [email]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // No email validation needed since it's read-only

    if (!formValues.code) newErrors.code = 'Code is required';

    // Name validation only if collection is needed
    if (needsNameCollection) {
      if (!formValues.firstName?.trim()) {
        newErrors.firstName = 'First name is required';
      }
      if (!formValues.lastName?.trim()) {
        newErrors.lastName = 'Last name is required';
      }
    }

    if (!formValues.password) {
      newErrors.password = 'Password is required';
    } else if (formValues.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (!/(?=.*\d)/.test(formValues.password)) {
      newErrors.password = "Passwords must have at least one digit ('0'-'9')";
    } else if (!/(?=.*[a-z])/.test(formValues.password)) {
      newErrors.password =
        "Passwords must have at least one lowercase ('a'-'z')";
    } else if (!/(?=.*[A-Z])/.test(formValues.password)) {
      newErrors.password =
        "Passwords must have at least one uppercase ('A'-'Z')";
    }

    if (formValues.password !== formValues.repeat_password)
      newErrors.repeat_password = 'Password not matched';

    return newErrors;
  };

  const changePassword = async () => {
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      setLoading(true);
      try {
        // Prepare arguments for API call
        const firstName = needsNameCollection
          ? formValues.firstName.trim()
          : null;
        const lastName = needsNameCollection
          ? formValues.lastName.trim()
          : null;

        // Use the email from form values (which should be the actual email address)
        await resetPassword(
          formValues.email,
          formValues.code,
          formValues.password,
          firstName,
          lastName
        );

        const successMessage = needsNameCollection
          ? 'Account profile completed successfully.'
          : memberInfo?.hasSetPassword
            ? 'Password reset successfully.'
            : 'Password set successfully.';

        toast.success(successMessage);
        navigate(routes.auth.login);
      } catch (e) {
        console.log('error: ', e);
        toast.error(
          e.response?.data?.title ||
            e.response?.data ||
            'Failed to update password'
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const getHeaderText = () => {
    if (needsNameCollection) {
      return 'Complete Your Account Profile';
    }
    if (memberInfo?.firstName && !memberInfo?.hasSetPassword) {
      return `Welcome, ${memberInfo.firstName}!`;
    }
    if (memberInfo?.firstName && memberInfo?.hasSetPassword) {
      return `Welcome back, ${memberInfo.firstName}!`;
    }
    if (!memberInfo?.hasSetPassword) {
      return 'Set Your Password';
    }
    return 'Reset Your Password';
  };

  const getSubtitleText = () => {
    if (needsNameCollection && !memberInfo?.hasSetPassword) {
      return 'Please complete your profile by providing your name and creating a secure password.';
    }
    if (needsNameCollection && memberInfo?.hasSetPassword) {
      return 'Please complete your profile by providing your name, then enter the verification code to reset your password.';
    }
    if (!memberInfo?.hasSetPassword) {
      return 'Enter the verification code from your email and create a secure password.';
    }
    return 'Enter the verification code from your email and create a new secure password.';
  };

  // Show loading state while checking member info
  if (loadingMemberInfo) {
    return (
      <>
        <MetaTags title="SplitFast | Loading..." />
        <div className={styles.resetPasswordContainer}>
          <div className={styles.headerSection}>
            <h1 className="heading1">Loading...</h1>
            <p className={styles.subtitle}>
              Please wait while we load your information.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <MetaTags title="SplitFast | Reset Password" />
      <div className={styles.resetPasswordContainer}>
        <div className={styles.headerSection}>
          <h1 className="heading1">{getHeaderText()}</h1>
          <p className={styles.subtitle}>{getSubtitleText()}</p>
        </div>

        {/* Always show email at the top as read-only */}
        <div className={styles.emailField}>
          <label className={styles.readOnlyLabel}>Email Address</label>
          <div className={styles.readOnlyValue}>{formValues.email}</div>
        </div>

        {/* Show name as read-only if available but not collecting */}
        {!needsNameCollection &&
          memberInfo &&
          (memberInfo.firstName || memberInfo.lastName) && (
            <div className={styles.nameInfoDisplay}>
              <label className={styles.readOnlyLabel}>Name</label>
              <div className={styles.readOnlyValue}>
                {memberInfo.firstName} {memberInfo.lastName}
              </div>
            </div>
          )}

        <div className={styles.inputSection}>
          {/* Name input fields if collection is needed */}
          {needsNameCollection && (
            <div className={styles.nameInputs}>
              <CustomInput
                label="First Name"
                name="firstName"
                type="text"
                placeholder="Enter your first name"
                value={formValues.firstName}
                error={errors.firstName}
                onChange={handleInputChange}
                required
                style={{ marginBottom: 20 }}
                inputStyle={{
                  fontSize: '15px',
                  height: '60px',
                }}
              />
              <CustomInput
                label="Last Name"
                name="lastName"
                type="text"
                placeholder="Enter your last name"
                value={formValues.lastName}
                error={errors.lastName}
                onChange={handleInputChange}
                required
                style={{ marginBottom: 20 }}
                inputStyle={{
                  fontSize: '15px',
                  height: '60px',
                }}
              />
            </div>
          )}

          {/* Hidden dummy password field to confuse browser autofill */}
          <input
            type="password"
            style={{ display: 'none' }}
            autoComplete="current-password"
            tabIndex="-1"
          />

          <CustomInput
            label="Verification Code"
            name="code"
            type="text"
            placeholder="Enter 6-digit verification code"
            value={formValues.code}
            error={errors.code}
            onChange={handleInputChange}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            style={{ marginBottom: 20 }}
            inputStyle={{
              fontSize: '15px',
              height: '60px',
            }}
          />

          <CustomInput
            label="New Password"
            name="new_password"
            type="password"
            placeholder="Type new password"
            value={formValues.password}
            error={errors.password}
            onChange={(e) =>
              setFormValues((prev) => ({ ...prev, password: e.target.value }))
            }
            autoComplete="new-password"
            style={{ marginBottom: 20 }}
            inputStyle={{
              fontSize: '15px',
              height: '60px',
            }}
          />

          <CustomInput
            label="Repeat New Password"
            name="confirm_new_password"
            type="password"
            placeholder="Confirm new password"
            value={formValues.repeat_password}
            error={errors.repeat_password}
            onChange={(e) =>
              setFormValues((prev) => ({
                ...prev,
                repeat_password: e.target.value,
              }))
            }
            autoComplete="new-password"
            style={{ marginBottom: 20 }}
            inputStyle={{
              fontSize: '15px',
              height: '60px',
            }}
          />
        </div>

        <div className={styles.helpText}>
          <p>
            <strong>Password Requirements:</strong> Your new password must be at
            least 8 characters long and contain at least 1 uppercase letter, 1
            lowercase letter, and 1 digit for security.
          </p>
        </div>

        <div className={styles.buttonSection}>
          <CustomButton
            disabled={loading}
            onClick={changePassword}
            style={{
              ...defaultButtonStyle,
              width: '100%',
              maxWidth: 400,
            }}
          >
            {loading
              ? needsNameCollection
                ? 'Completing profile...'
                : 'Updating password...'
              : needsNameCollection
                ? 'Complete Profile'
                : memberInfo?.hasSetPassword
                  ? 'Reset Password'
                  : 'Set Password'}
          </CustomButton>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
